import { undoDepth, redoDepth } from '@tiptap/pm/history';
import { Mark, MarkType } from '@tiptap/pm/model';
import { EditorState, TextSelection } from '@tiptap/pm/state';
import { Editor } from '@tiptap/react';

import { TIconName } from '@payaca/untitled-icons';

export type EditorToolbarItem =
  | 'bold'
  | 'italic'
  | 'strike'
  | 'underline'
  | 'code'
  | 'codeBlock'
  | 'link'
  | 'unlink'
  | 'bulletList'
  | 'orderedList'
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'blockquote'
  | 'undo'
  | 'redo';

export interface EditorToolbarAction {
  label: string;
  icon: TIconName;
  isActive?: (editor: Editor) => boolean;
  onClick: (editor: Editor) => void;
  disabled?: (editor: Editor) => boolean;
}

/**
 * Default toolbar shared by `RichTextarea` and `MarkdownTextarea`. Items beyond
 * this set (headings, blockquote, code, etc.) are available via the `toolbar`
 * prop.
 */
export const DEFAULT_TOOLBAR: EditorToolbarItem[] = [
  'bold',
  'italic',
  'strike',
  'bulletList',
  'orderedList',
  'link',
  'unlink',
  'undo',
  'redo',
];

/**
 * Default toolbar for the borderless/inline editor, surfaced as a bubble menu
 * on text selection. Limited to selection-relevant actions — undo/redo and
 * other document-level commands don't belong on a selection bubble.
 */
export const DEFAULT_INLINE_TOOLBAR: EditorToolbarItem[] = [
  'bold',
  'italic',
  'strike',
  'bulletList',
  'orderedList',
  'link',
  'unlink',
];

type ChainCommands = ReturnType<Editor['chain']>;

/**
 * Mirrors the check in TipTap's `setMark`, without the cost of building an
 * `editor.can()` probe on every transaction.
 */
const canSetMark = (state: EditorState, type: MarkType): boolean => {
  const marksAllow = (marks: readonly Mark[]) =>
    !!type.isInSet(marks) || !marks.some((mark) => mark.type.excludes(type));

  const { selection } = state;
  if (selection instanceof TextSelection && selection.$cursor) {
    const { $cursor } = selection;
    return (
      $cursor.parent.type.allowsMarkType(type) &&
      marksAllow(state.storedMarks ?? $cursor.marks())
    );
  }

  let applies = false;
  state.doc.nodesBetween(selection.from, selection.to, (node, _pos, parent) => {
    if (applies) return false;
    applies =
      node.isInline &&
      (!parent || parent.type.allowsMarkType(type)) &&
      marksAllow(node.marks);
  });
  return applies;
};

const markAction = (
  label: string,
  icon: TIconName,
  mark: string,
  exec: (chain: ChainCommands) => ChainCommands
): EditorToolbarAction => ({
  label,
  icon,
  isActive: (editor) => editor.isActive(mark),
  onClick: (editor) => exec(editor.chain().focus()).run(),
  disabled: (editor) => {
    if (editor.isActive(mark)) return false;
    const type = editor.schema.marks[mark];
    return !type || !canSetMark(editor.state, type);
  },
});

/**
 * No `disabled` check: TipTap clears the current block before converting it,
 * so these succeed from headings, lists and code blocks even where
 * `editor.can()` reports they would fail.
 */
const nodeAction = (
  label: string,
  icon: TIconName,
  node: string,
  exec: (chain: ChainCommands) => ChainCommands,
  isActiveOptions?: Record<string, unknown>
): EditorToolbarAction => ({
  label,
  icon,
  isActive: (editor) => editor.isActive(node, isActiveOptions),
  onClick: (editor) => exec(editor.chain().focus()).run(),
});

export const EDITOR_TOOLBAR_ACTIONS: Record<
  EditorToolbarItem,
  EditorToolbarAction
> = {
  bold: markAction('Bold', 'bold-01.3', 'bold', (c) => c.toggleBold()),
  italic: markAction('Italic', 'italic-01.3', 'italic', (c) =>
    c.toggleItalic()
  ),
  strike: markAction('Strikethrough', 'strikethrough-01.3', 'strike', (c) =>
    c.toggleStrike()
  ),
  underline: markAction('Underline', 'underline-01.3', 'underline', (c) =>
    c.toggleUnderline()
  ),
  code: markAction('Inline code', 'brackets.3', 'code', (c) => c.toggleCode()),
  codeBlock: nodeAction('Code block', 'brackets.3', 'codeBlock', (c) =>
    c.toggleCodeBlock()
  ),
  link: {
    label: 'Link',
    icon: 'link-01.3',
    isActive: (editor) => editor.isActive('link'),
    onClick: (editor) => {
      const linkHref: unknown = editor.getAttributes('link').href;
      const previousUrl = typeof linkHref === 'string' ? linkHref : '';
      const promptResult = window.prompt('URL', previousUrl);
      if (promptResult === null) return;
      const url = promptResult.trim();
      if (url === '') {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }
      // With no selection and not in a link, `extendMarkRange` is a no-op
      // and `setLink` would silently fail. Insert the URL as linked text
      // instead so the toolbar action always has a visible effect.
      if (editor.state.selection.empty && !editor.isActive('link')) {
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'text',
            text: url,
            marks: [{ type: 'link', attrs: { href: url, target: '_blank' } }],
          })
          .run();
        return;
      }
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url, target: '_blank' })
        .run();
    },
  },
  unlink: {
    label: 'Unlink',
    icon: 'link-broken-01.3',
    onClick: (editor) => editor.chain().focus().unsetLink().run(),
    disabled: (editor) => !editor.isActive('link'),
  },
  bulletList: nodeAction('Bullet list', 'dotpoints-01.3', 'bulletList', (c) =>
    c.toggleBulletList()
  ),
  orderedList: nodeAction('Ordered list', 'list-ordered', 'orderedList', (c) =>
    c.toggleOrderedList()
  ),
  heading1: nodeAction(
    'Heading 1',
    'hash-02.3',
    'heading',
    (c) => c.toggleHeading({ level: 1 }),
    { level: 1 }
  ),
  heading2: nodeAction(
    'Heading 2',
    'hash-02.3',
    'heading',
    (c) => c.toggleHeading({ level: 2 }),
    { level: 2 }
  ),
  heading3: nodeAction(
    'Heading 3',
    'hash-02.3',
    'heading',
    (c) => c.toggleHeading({ level: 3 }),
    { level: 3 }
  ),
  blockquote: {
    ...nodeAction('Blockquote', 'message-text-square-01.3', 'blockquote', (c) =>
      c.toggleBlockquote()
    ),
    disabled: (editor) => !editor.can().toggleBlockquote(),
  },
  undo: {
    label: 'Undo',
    icon: 'flip-backward.3',
    onClick: (editor) => editor.chain().focus().undo().run(),
    disabled: (editor) => undoDepth(editor.state) === 0,
  },
  redo: {
    label: 'Redo',
    icon: 'flip-forward.3',
    onClick: (editor) => editor.chain().focus().redo().run(),
    disabled: (editor) => redoDepth(editor.state) === 0,
  },
};

export interface EditorToolbarItemState {
  item: EditorToolbarItem;
  active: boolean;
  disabled: boolean;
}

export const getToolbarState = (
  editor: Editor,
  items: readonly EditorToolbarItem[]
): EditorToolbarItemState[] =>
  items.map((item) => {
    const action = EDITOR_TOOLBAR_ACTIONS[item];
    return {
      item,
      active: action.isActive?.(editor) ?? false,
      disabled: action.disabled?.(editor) ?? false,
    };
  });
